import { useEffect } from 'react'
import { NavLink, Outlet, useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { useAuth } from '../auth/useAuth'
import { loadAuthConfig } from '../lib/authConfig'
import { useSignOut } from '../hooks/useSignOut'
import { NotificationsMenu } from '../components/NotificationsMenu'
import { LanguageMenu } from '../components/LanguageMenu'
import { Breadcrumbs } from '../components/Breadcrumbs'
import { useTheme } from './useTheme'
import { TOPBAR_BTN_CLASS } from './topbarButton'
import { PAGES, pagePath, type PageSlug } from '../lib/routes'
import { useCrumbTrail } from '../hooks/useCrumbTrail'
import { PageIcon } from '../components/PageIcon'
import { Toaster } from '../components/ui/Toast'
import { confirmDiscardIfDirty } from '../hooks/useUnsavedGuard'
import { measureScrollbarWidth } from '../lib/scrollbarWidth'

// Three groups, matching the prototype's own left-nav grouping — MANAGE is
// what you change, CONTROL is what you constrain, OBSERVE is what happened
// (see the prototype's own comment on this same grouping, also mirrored on
// Home). Monthly Limits / Tenant Limits carry a "Preview" badge — real nav
// destinations, but the pages behind them are Preview-only stubs (see
// ARCHITECTURE_DECISIONS.md). Paths and labels come from lib/routes.ts.
const NAV_GROUPS: ReadonlyArray<{
  labelKey: 'appShell.nav.manage' | 'appShell.nav.control' | 'appShell.nav.observe'
  items: ReadonlyArray<{ page: PageSlug; preview?: boolean }>
}> = [
  {
    labelKey: 'appShell.nav.manage',
    items: [
      { page: 'subscription' },
      { page: 'additional-volume' },
      { page: 'excess-volume' },
    ],
  },
  {
    labelKey: 'appShell.nav.control',
    items: [
      { page: 'user-limits' },
      { page: 'monthly-limits', preview: true },
      { page: 'tenant-limits', preview: true },
    ],
  },
  {
    labelKey: 'appShell.nav.observe',
    items: [{ page: 'usage' }],
  },
]

function HomeIcon() {
  return (
    <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
      <path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z" />
      <polyline points="9 22 9 12 15 12 15 22" />
    </svg>
  )
}

// One class string for every nav item (prototype `.nav-item` / `.active`).
function navItemClass({ isActive }: Readonly<{ isActive: boolean }>): string {
  return `flex items-center gap-2.5 px-3 py-2.5 rounded-md text-[13px] font-medium transition-colors ${
    isActive ? 'bg-white/15 text-white font-semibold' : 'text-white/70 hover:bg-white/10 hover:text-white'
  }`
}

function initials(name: string | null, email: string | null): string {
  const source = name || email || ''
  const parts = source.split(/[\s@.]+/).filter(Boolean)
  return (parts[0]?.[0] ?? '') .concat(parts[1]?.[0] ?? '').toUpperCase() || '?'
}

export function AppShell() {
  const { t } = useTranslation()
  const { userName, userEmail, accountName, isPortalAdmin } = useAuth()
  const { isDark, toggle } = useTheme()
  const navigate = useNavigate()
  const signOut = useSignOut('/')
  const hasCrumb = useCrumbTrail() !== null

  // Read the auth config (MS-977) once while signed in, so sign-out answers
  // from the cache rather than waiting on GET /auth/config. A failed read is
  // fine here: sign-out then falls back to its own bounded read.
  useEffect(() => {
    loadAuthConfig().catch(() => {
      // See above.
    })
  }, [])

  // `--sbw`, which every page's scroll area and the User Limits list use to
  // line their content up with the fixed header on classic scrollbars.
  useEffect(() => {
    measureScrollbarWidth()
  }, [])

  async function handleLogout() {
    // The prototype's confirmDiscardIfDirty runs on logout too: signing out
    // with unsaved edits asks first, and Cancel keeps the session (MS-819
    // shared primitives, useUnsavedGuard).
    if (!confirmDiscardIfDirty(t('common.unsavedChangesConfirm'))) return
    // The prototype's sign-out confirm (MS-993) — coming back means
    // authenticating again. Cancel keeps the session.
    if (!globalThis.confirm(t('appShell.signOutConfirm'))) return
    await signOut()
  }

  return (
    <div className="h-screen flex flex-col bg-[var(--bg-app)] text-[var(--text-primary)]">
      {/* ─── Top bar ─── */}
      <header
        className="h-14 flex items-center justify-between px-5 gap-4 text-white shrink-0 z-10"
        style={{ background: 'var(--header-bg)' }}
      >
        <div className="flex items-center gap-2.5 min-w-0 flex-1">
          <button
            className="flex items-center gap-2.5 mr-5 shrink-0 cursor-pointer bg-transparent border-none text-white"
            onClick={() => navigate('/')}
          >
            <img src="/assets/mambu-logo.png" alt="Mambu" className="w-[26px] h-[26px] object-contain" />
            <span className="text-[16.5px] font-bold tracking-tight whitespace-nowrap">
              {t('appShell.brand')}
            </span>
          </button>
          {accountName && (
            <span className="flex items-center gap-2 text-[13px] font-semibold text-white/90 min-w-0">
              <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="rgba(255,255,255,.75)" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round">
                <rect x="3" y="3" width="12" height="18" rx="1.5" />
                <path d="M15 8h5a1.5 1.5 0 0 1 1.5 1.5V21H15" />
                <line x1="6.5" y1="7" x2="8" y2="7" />
                <line x1="10" y1="7" x2="11.5" y2="7" />
                <line x1="6.5" y1="11" x2="8" y2="11" />
                <line x1="10" y1="11" x2="11.5" y2="11" />
                <line x1="6.5" y1="15" x2="8" y2="15" />
                <line x1="10" y1="15" x2="11.5" y2="15" />
                <line x1="18" y1="12" x2="18.5" y2="12" />
                <line x1="18" y1="16" x2="18.5" y2="16" />
              </svg>
              <span className="truncate">{accountName}</span>
            </span>
          )}
        </div>
        <div className="flex items-center gap-2.5 shrink-0">
          <button
            onClick={toggle}
            className={TOPBAR_BTN_CLASS}
            title={t('appShell.toggleTheme')}
            aria-label={t('appShell.toggleTheme')}
          >
            {isDark ? (
              <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" strokeWidth="1.8"><circle cx="12" cy="12" r="5" /><line x1="12" y1="1" x2="12" y2="3" /><line x1="12" y1="21" x2="12" y2="23" /><line x1="4.22" y1="4.22" x2="5.64" y2="5.64" /><line x1="18.36" y1="18.36" x2="19.78" y2="19.78" /><line x1="1" y1="12" x2="3" y2="12" /><line x1="21" y1="12" x2="23" y2="12" /><line x1="4.22" y1="19.78" x2="5.64" y2="18.36" /><line x1="18.36" y1="5.64" x2="19.78" y2="4.22" /></svg>
            ) : (
              <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" strokeWidth="1.8"><path d="M21 12.79A9 9 0 1 1 11.21 3 7 7 0 0 0 21 12.79z" /></svg>
            )}
          </button>
          <LanguageMenu />
          <NotificationsMenu />
          <button onClick={handleLogout} className={TOPBAR_BTN_CLASS} title={t('appShell.signOut')} aria-label={t('appShell.signOut')}>
            <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
              <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4" />
              <polyline points="16 17 21 12 16 7" />
              <line x1="21" y1="12" x2="9" y2="12" />
            </svg>
          </button>
          <div className="flex items-center gap-2 px-2 py-1">
            <span className="w-[30px] h-[30px] rounded-full bg-white/20 flex items-center justify-center text-[11px] font-bold">
              {initials(userName, userEmail)}
            </span>
            <span className="text-[13px] font-semibold text-white/90 hidden sm:inline">{userName ?? userEmail}</span>
          </div>
        </div>
      </header>

      <div className="flex-1 min-h-0 flex">
        {/* ─── Left nav ─── */}
        <nav className="w-[260px] min-w-[260px] flex flex-col text-white" style={{ background: 'var(--nav-bg)' }}>
          <div className="flex-1 px-2.5 pt-3.5 pb-3 flex flex-col gap-0.5">
            <NavLink to="/" end className={navItemClass}>
              <HomeIcon />
              {t('appShell.nav.home')}
            </NavLink>

            {NAV_GROUPS.map((group) => (
              <div key={group.labelKey}>
                <div className="text-[10px] font-semibold uppercase tracking-wider text-white/40 px-3 pt-4 pb-1.5">
                  {t(group.labelKey)}
                </div>
                {group.items.map((item) => (
                  // No `end`: the item stays active on its `/{page}/{tab}`
                  // sub-paths. The bare path redirects to the default tab.
                  <NavLink key={item.page} to={pagePath(item.page)} className={navItemClass}>
                    <PageIcon page={item.page} size={18} stroke="currentColor" />
                    <span className="flex-1">{t(PAGES[item.page].labelKey)}</span>
                    {item.preview && (
                      // `.nav-badge-soon`: 9px/600, not uppercased, .3px tracking,
                      // 1px 6px padding, 8px radius, translucent white on the
                      // navy sidebar in both themes.
                      <span className="text-[9px] leading-[normal] font-semibold tracking-[.3px] text-white/60 bg-white/15 px-1.5 py-px rounded-lg">
                        {t('common.previewBadge')}
                      </span>
                    )}
                  </NavLink>
                ))}
              </div>
            ))}

            {/* Settings sits apart from the three domains, at the foot of the
                navigation below a hairline (prototype `.nav-bottom` /
                `.nav-bottom-divider`, MS-997): it configures the portal
                itself. Admins only (§1-ctrl-vis), never a card on Home. */}
            {isPortalAdmin && (
              <div data-testid="nav-bottom" className="mt-auto flex flex-col">
                <div data-testid="nav-bottom-divider" className="h-px bg-white/[.14] mx-3 mt-3 mb-2.5" />
                <NavLink to={pagePath('settings')} className={navItemClass}>
                  <PageIcon page="settings" size={18} stroke="currentColor" />
                  <span className="flex-1">{t(PAGES.settings.labelKey)}</span>
                </NavLink>
              </div>
            )}
          </div>
        </nav>

        {/* ─── Main area ─── */}
        <div className="flex-1 flex flex-col overflow-hidden">
          <Breadcrumbs />
          {/* 16px from the crumb bar to the page title (AC3; prototype
              section `padding-top:16px`), 24px on Home, which has no crumb.
              It does not scroll itself (MS-996): each page keeps its header
              and tabs fixed and scrolls only its PageScrollArea, the
              prototype's `.tab-scroll-area`. */}
          <main
            data-testid="app-main"
            className={`flex-1 min-h-0 flex flex-col overflow-hidden px-8 ${hasCrumb ? 'pt-4' : 'pt-6'}`}
          >
            <Outlet />
          </main>
        </div>
      </div>
      <Toaster />
    </div>
  )
}
